/**
 * Project store — Zustand.
 *
 * Хранит currentProjectId — проект, в котором сейчас работает пользователь.
 * Реактивно подписан во всех picker'ах (FilesUpload, FilesResults, generation,
 * search) и в Header. Синхронизируется с localStorage через DEFAULT_PROJECT_KEY
 * для сохранения выбора между сессиями.
 *
 * Хранит activeDatasetId — датасет проекта, выбранный пользователем как
 * «в работе» (project.active_dataset_id на бэкенде). Phase 2.4 (Files UX
 * Refactor 2026-08-06) добавит загрузку через GET /projects/{id} и setter,
 * синхронизированный с PATCH /projects/{id}/active-dataset.
 *
 * Принцип: picker в Header — единственный источник истины для смены проекта.
 * Другие места только ЧИТАЮТ currentProjectId через useProjectStore.
 */
import { create } from 'zustand';

import { DEFAULT_PROJECT_KEY } from '@/lib/projectStorage';

interface ProjectState {
  currentProjectId: string | null;
  activeDatasetId: string | null;

  setCurrent: (projectId: string | null) => void;
  setActiveDataset: (datasetId: string | null) => void;
  reset: () => void;
}

function readPersisted(): string | null {
  try {
    return localStorage.getItem(DEFAULT_PROJECT_KEY);
  } catch {
    return null;
  }
}

const initial = readPersisted();

export const useProjectStore = create<ProjectState>((set) => ({
  currentProjectId: initial,
  activeDatasetId: null,

  setCurrent: (projectId) => {
    if (projectId) {
      localStorage.setItem(DEFAULT_PROJECT_KEY, projectId);
    } else {
      localStorage.removeItem(DEFAULT_PROJECT_KEY);
    }
    set({ currentProjectId: projectId, activeDatasetId: null });
  },

  setActiveDataset: (datasetId) => set({ activeDatasetId: datasetId }),

  reset: () => {
    localStorage.removeItem(DEFAULT_PROJECT_KEY);
    set({ currentProjectId: null, activeDatasetId: null });
  },
}));
